import React from 'react'

type KeyPressHandler = {
  id: string
  key?: string
  handler: (event: KeyboardEvent) => boolean | void // Return true to mark as handled
}

class KeyPressRegistry {
  private handlers: KeyPressHandler[] = []

  private isListening = false

  private nextId = 0

  register(handler: (event: KeyboardEvent) => boolean | void, key?: string): () => void {
    const handlerId = `handler-${this.nextId++}`
    this.handlers.push({id: handlerId, key, handler})

    if (!this.isListening) {
      this.startListening()
    }

    return () => {
      this.handlers = this.handlers.filter(h => h.id !== handlerId)
      if (this.handlers.length === 0) {
        this.stopListening()
      }
    }
  }

  private startListening() {
    this.isListening = true
    window.addEventListener('keydown', this.handleKeyDown, false)
  }

  private stopListening() {
    this.isListening = false
    window.removeEventListener('keydown', this.handleKeyDown, false)
  }

  private handleKeyDown = (event: KeyboardEvent) => {
    // Process handlers in reverse order (stack behavior - last registered = first called)
    this.handlers
      .slice()
      .reverse()
      .find(handler => {
        // Check if key matches (or no key filter specified)
        if (handler.key === undefined || handler.key === event.key) {
          const wasHandled = handler.handler(event)
          return wasHandled === true
        }
        return false
      })
  }
}

const keyPressRegistry = new KeyPressRegistry()

const useKeyPress = (
  onKeyPress: (event: KeyboardEvent) => boolean | void,
  key?: string,
  enabled = true
) => {
  const onKeyPressEvent = React.useEffectEvent(onKeyPress)

  React.useEffect(() => {
    if (!enabled) {
      return undefined
    }

    return keyPressRegistry.register(event => onKeyPressEvent(event), key)
  }, [enabled, key])
}

export default useKeyPress
